import styled, { css } from 'styled-components';
import { IGutterProps, SPACE_DEFAULT, useGutterCss } from '../../fundamentals/spaces/space';
import {
  DisplayCss,
  FONT_SIZES,
  FONT_WEIGHTS,
  useDisplayCss,
  useFontSizeCss,
  useFontWeightCss,
  useTypographyCss,
  ITypographyProps,
  IMarketingFontProps,
  ITextAlignProps,
  IFontWeightProps,
  IDisplayCssProps,
  IDisplayType,
  INoLineHeightProps
} from '../../fundamentals/typography/typography';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { useActionOnTextCss } from '../../fundamentals/actions';
import { BaseH1, BaseH2, BaseH3, BaseH4, BaseH5, BaseH6 } from '../../common/BaseElements';
import { TYPOGRAPHY } from '../../fundamentals/typography/eoraTypography';
import { displayTypes, useEoraCss, useEoraMobileCss } from '../../fundamentals/typography/typographyEora';

const useClickableHeading = css<{ onClick?: React.MouseEventHandler }>`
  ${useActionOnTextCss};
  ${({ onClick }: { onClick?: React.MouseEventHandler }) =>
    onClick &&
    css`
      &:hover {
        color: ${({ theme }) => theme.colours.links.hover};
      }
      &:active {
        color: ${({ theme }) => theme.colours.links.active};
      }
    `};
`;

export interface IHeadingEoraProps {
  headingType?: keyof (typeof TYPOGRAPHY.HEADINGS)[keyof typeof TYPOGRAPHY.HEADINGS];
  underlined?: boolean;
  lineHeight?: number;
}

export type IHeadingCssProps = IGutterProps &
  IMarketingFontProps &
  ITextAlignProps &
  IFontWeightProps &
  IDisplayCssProps & { onClick?: React.MouseEventHandler } & IDisplayType &
  INoLineHeightProps;

export const useHeadingCss = css<IHeadingEoraProps & IHeadingCssProps>`
  ${useTypographyCss};
  ${useGutterCss({ bottom: SPACE_DEFAULT })};
  ${useDisplayCss(DisplayCss.Block)};
  ${useClickableHeading};

  ${({ headingType }) =>
    !headingType &&
    css`
      ${useFontWeightCss(FONT_WEIGHTS.SEMI_BOLD)};
      letter-spacing: -0.02rem;
    `}
`;

export const Heading1 = styled(BaseH1)<IHeadingEoraProps & ITypographyProps & IHeadingCssProps>`
  ${useHeadingCss};

  ${({ headingType, underlined, lineHeight, asDisplay }) => {
    if (headingType) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][headingType], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.HEADINGS.HEADING_1[headingType], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_HEADING_1[headingType])}
          `;
    } else {
      return css`
        ${useFontSizeCss({
          [SIZES.X_SMALL]: FONT_SIZES.HEADING_2,
          [SIZES.SMALL]: FONT_SIZES.HEADING_1
        })}
      `;
    }
  }}
`;

export const Heading2 = styled(BaseH2)<IHeadingEoraProps & ITypographyProps & IHeadingCssProps>`
  ${useHeadingCss};

  ${({ headingType, underlined, lineHeight, asDisplay }) => {
    if (headingType) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][headingType], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.HEADINGS.HEADING_2[headingType], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_HEADING_2[headingType])}
          `;
    } else {
      return css`
        ${useFontSizeCss({
          [SIZES.X_SMALL]: FONT_SIZES.HEADING_3,
          [SIZES.SMALL]: FONT_SIZES.HEADING_2
        })}
      `;
    }
  }}
`;

export const Heading3 = styled(BaseH3)<IHeadingEoraProps & ITypographyProps & IHeadingCssProps>`
  ${useHeadingCss};

  ${({ headingType, underlined, lineHeight, asDisplay }) => {
    if (headingType) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][headingType], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.HEADINGS.HEADING_3[headingType], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_HEADING_3[headingType])}
          `;
    } else {
      return css`
        ${useFontSizeCss({
          [SIZES.X_SMALL]: FONT_SIZES.HEADING_4,
          [SIZES.SMALL]: FONT_SIZES.HEADING_3
        })}
      `;
    }
  }}
`;

export const Heading4 = styled(BaseH4)<IHeadingEoraProps & ITypographyProps & IHeadingCssProps>`
  ${useHeadingCss};

  ${({ headingType, underlined, lineHeight, asDisplay }) => {
    if (headingType) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][headingType], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.HEADINGS.HEADING_4[headingType], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_HEADING_4[headingType])}
          `;
    } else {
      return css`
        ${useFontSizeCss({
          [SIZES.X_SMALL]: FONT_SIZES.HEADING_5,
          [SIZES.SMALL]: FONT_SIZES.HEADING_4
        })}
      `;
    }
  }}
`;

export const Heading5 = styled(BaseH5)<IHeadingEoraProps & ITypographyProps & IHeadingCssProps>`
  ${useHeadingCss};

  ${({ headingType, underlined, lineHeight, asDisplay }) => {
    if (headingType) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][headingType], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.HEADINGS.HEADING_5[headingType], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_HEADING_5[headingType])}
          `;
    } else {
      return css`
        ${useFontSizeCss({
          [SIZES.X_SMALL]: FONT_SIZES.HEADING_5_MOBILE,
          [SIZES.SMALL]: FONT_SIZES.HEADING_5
        })}
      `;
    }
  }}
`;

export const Heading6 = styled(BaseH6)<IHeadingEoraProps & ITypographyProps & IHeadingCssProps>`
  ${useHeadingCss}

  ${({ headingType, underlined, lineHeight, asDisplay }) => {
    if (headingType) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][headingType], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.HEADINGS.HEADING_6[headingType], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_HEADING_6[headingType])}
          `;
    } else {
      return css`
        ${useFontSizeCss({
          [SIZES.SMALL]: FONT_SIZES.HEADING_6
        })}
      `;
    }
  }}
`;
